import { Box, Typography, useTheme } from '@mui/material';
import {
  ExperienceModel,
  Specialty,
  useGetExperience,
} from '@/logic/services/experiences';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import React, { useMemo } from 'react';

import { CheckGlobalSsrErrorsOrFail } from '@/helpers/check-global-ssr-errors';
import ExperienceSpecialitiesSlider from '@/components/experience/experiences-specialities-slider';
import { MovieTypesEnum } from '@/constants/movie-types-enum';
import MoviesList from '@/components/shared/movies-list';
import Seo from '@/components/seo/seo';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { useGetInfiniteMovies } from '@/logic/services';
import { useRouter } from 'next/router';
import ImageBoxFuture from '@/components/shared/image-box-future';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const paginationShowing = {
  limit: 4,
  page: 1,
};

export const ExperienceDetails: NextPage<{
  path: string;
}> = ({ path }) => {
  const { query } = useRouter();
  const { cityId: browserCityId } = useCity();
  const cityId = +query.cityId || browserCityId;

  const theme = useTheme();
  const intl = useIntl();

  const { data: experienceData } = useGetExperience({
    query: { slug: query.slug as string },
    params: {
      cityId: cityId,
    },
  });

  const { data, isError, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useGetInfiniteMovies(
      {
        params: {
          experienceIds: [experienceData?.experience.id as number],
          cityId: cityId,
          limit: paginationShowing.limit,
          page: paginationShowing.page,
          type: MovieTypesEnum.SHOW_NOW,
        },
      },
      { enabled: !!experienceData }
    );

  const movies = useMemo(
    () => (data?.pages ? data?.pages?.flatMap(group => group.data) : []),
    [data]
  );

  return (
    <>
      <Seo
        title={
          experienceData?.experience?.titleTag ||
          intl.formatMessage({ id: 'experience_details' })
        }
        canonical={experienceData?.experience?.canonicalURL}
        pagePath={path}
        description={
          experienceData?.experience?.descriptionTag ||
          experienceData?.experience?.description
        }
        image={experienceData?.experience?.cover}
        hasFollow={experienceData?.experience?.hasFollow}
        hasIndex={experienceData?.experience?.hasIndex}
      />

      <Box
        component='div'
        sx={{
          flexGrow: 1,
          mb: pxToRem(122),
          '& h1,h2,h3,h4,h5': {
            fontWeight: 700,
          },
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: pxToRem(30),
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            px: {
              laptop: pxToRem(128),
              tablet: pxToRem(60),
              mobile: '18px',
            },
            maxWidth: '1920px',
            mx: 'auto',
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='experience_details' />
        </Typography>
        {!isError && (
          <Box component='div' sx={{}}>
            {experienceData?.experience?.cover && (
              <Box component='section' sx={{ position: 'relative' }}>
                <ImageBoxFuture
                  img={experienceData?.experience?.cover}
                  title={
                    experienceData?.experience?.coverAlt ||
                    experienceData?.experience?.name
                  }
                  layout='intrinsic'
                  objectFit='cover'
                  width='1920px'
                  height='1080px'
                  sx={{
                    marginBottom: pxToRem(32),
                  }}
                />
              </Box>
            )}
            <Box sx={{ maxWidth: '1920px', mx: 'auto' }}>
              <Box
                component='section'
                sx={{
                  px: {
                    laptop: pxToRem(128),
                    tablet: pxToRem(60),
                    mobile: '16px',
                  },
                  mb: pxToRem(48),
                }}
              >
                <Box component='div'>
                  <Typography
                    component='h4'
                    sx={{
                      fontSize: { tablet: pxToRem(24), mobile: '16px' },
                      color: theme.palette.grey[90],
                      mb: pxToRem(48),
                    }}
                  >
                    <FormattedMessage id='experience_details.about' />
                  </Typography>
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{
                      fontSize: { tablet: pxToRem(24), mobile: '16px' },
                      color: theme.palette.grey[50],
                      maxWidth: '1096px',
                      lineHeight: '150%',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {experienceData?.experience?.description}
                  </Typography>
                </Box>
              </Box>

              {experienceData?.experience?.specialties.length > 0 && (
                <Box
                  component='section'
                  sx={{
                    overflow: 'hidden',
                    px: {
                      laptop: pxToRem(128 - 15),
                      tablet: pxToRem(60),
                      mobile: '16px',
                    },
                  }}
                >
                  <Typography
                    component='h4'
                    sx={{
                      fontSize: { tablet: pxToRem(24), mobile: '16px' },

                      color: theme.palette.grey[90],
                      mb: pxToRem(32),
                      px: {
                        laptop: pxToRem(15),
                        tablet: 0,
                      },
                    }}
                  >
                    <FormattedMessage id='experience_details.available_experience_specialities' />
                  </Typography>

                  <ExperienceSpecialitiesSlider
                    Specialty={experienceData?.experience?.specialties as Specialty[]}
                  />
                </Box>
              )}
              {movies.length > 0 && (
                <Box
                  component='div'
                  sx={{
                    px: {
                      laptop: pxToRem(128),
                      tablet: pxToRem(60),
                      mobile: '16px',
                    },
                    mb: pxToRem(500),
                  }}
                >
                  <Typography
                    component='h4'
                    sx={{
                      fontSize: pxToRem(24),
                      color: theme.palette.grey[90],
                      mb: { tablet: pxToRem(59), mobile: '16px' },
                    }}
                  >
                    <FormattedMessage id='experience_details.movies_showing_in' />
                    {experienceData?.experience?.name}
                  </Typography>

                  <MoviesList
                    moviesList={movies}
                    hasMore={hasNextPage}
                    fetchMovies={fetchNextPage}
                    isFetching={isFetching}
                    isFetchingNextPage={isFetchingNextPage}
                    sx={{ mt: { mobile: '0px' } }}
                    itemListName='Experiences'
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const cityId = Number(context.query.cityId || context.req.cookies.city);

  const queryClient = new QueryClient();
  let experience: ExperienceModel;
  try {
    experience = await useGetExperience.fetchQuery(queryClient, {
      query: { slug: context.query.slug as string },
      params: {
        cityId: cityId,
      },
      headers,
    });
    if (experience?.id)
      await useGetInfiniteMovies.fetchQuery(queryClient, {
        params: {
          experienceIds: [experience.id],
          cityId: cityId,
          type: MovieTypesEnum.SHOW_NOW,
          limit: paginationShowing.limit,
          page: paginationShowing.page,
        },
        headers,
      });
  } catch (error) {
    return CheckGlobalSsrErrorsOrFail(error);
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default ExperienceDetails;
